import { Viewer } from '../Viewer';
import * as THREE from 'three';
import { HsbVisual } from '../../hsb-visual';
import { CameraView } from '../../isharedata';
import { Bitflag } from '../Helpers/Bitflag';
import { EventEmitter } from 'events';
import { lerp } from 'three/src/math/MathUtils';
import { HsbEntity, SceneLayerName } from '../../hsbcad-model';
import { Sides } from '../ViewCube';
import  HSBControls  from '../Camera/HSBControls';
import { ViewerScene } from '../ViewerScene';

/**
 * Flag that indicates what camera operation to perform.
 */
export enum TweenAttributes {
    NONE = 0,
    POSITION = 1 << 0,
    ROTATION = 1 << 1,
    ZOOM = 1 << 2,
    TARGET = 1 << 3,
    UP = 1 << 4,
    SPHERICAL = 1 << 5,
    ALL = ~NONE,
}

export interface FocusOptions {
    offset: number;
    tween: boolean;
    time: number;
    sides: Sides;
    direction: THREE.Vector3;
    setCameraDimensions: boolean;
    camera: THREE.OrthographicCamera | THREE.PerspectiveCamera;
    controls: HSBControls;
    scene: ViewerScene;
    onComplete: (...props: any) => any;
}

/**
 * Class the is used for any interactions withthe camera. Can handle all kinds of transformations and zooming.
 */
export class CameraUtils {
    deltaPos?: [THREE.Vector3, THREE.Vector3];
    deltaRot?: [THREE.Quaternion, THREE.Quaternion];
    deltaZoom?: [number, number];
    deltaTarget?: [THREE.Vector3, THREE.Vector3];
    deltaSpherical?: [THREE.Spherical, THREE.Spherical];
    deltaUp?: [THREE.Vector3, THREE.Vector3];
    startPos?: THREE.Vector3;
    targetPos?: THREE.Vector3;
    _tween: number = 0;
    _tweenAttributes: Bitflag<TweenAttributes> = new Bitflag<TweenAttributes>();
    tweenProgress;

    private abortEvent: EventEmitter = new EventEmitter();

    private _tweenFocus: any;
    private _tweenTime: number = 0.5;
    private _tweenStep: number = 60;
    private _tweenPath: THREE.Curve<THREE.Vector3> | null = null;
    private _tweenPromise?: Promise<boolean>;
    startRot?: THREE.Quaternion;
    targetRot?: THREE.Quaternion;
    targetZoom: any;
    startZoom?: number;
    frame: number = 0;
    endTarget?: THREE.Vector3;
    startTarget?: THREE.Vector3;
    maxPolarAngle: number = 0.001 * Math.PI;
    private _tweenInterval: any | null | ReturnType<typeof setInterval>;
    private _tweenTimeout: NodeJS.Timeout | null = null;
    frameTiming: number = 0;

    constructor(private _viewer: Viewer) {
        let self = this;
        self._viewer.currentControls?.addEventListener('start', () => {
            self.stopActiveOperation();
            self.calculateCentreOfMass();
        });
        this._viewer.SceneManager._sceneEvent.on('change', () => {
            self._viewer.currentControls?.addEventListener('start', () => {
                self.stopActiveOperation();
            });
        });
    }

    public dispose() {
        let self = this;
        this._viewer.currentControls?.removeEventListener('start', () => {
            self.stopActiveOperation();
            self.calculateCentreOfMass();
        });
        this._viewer.SceneManager._sceneEvent.removeListener('change', () => {
            self._viewer.currentControls?.addEventListener('start', () => {
                self.stopActiveOperation();
            });
        });
        self._viewer.currentControls?.removeEventListener('start', () => {
            self.stopActiveOperation();
        });
        // self._viewer.ViewerEvents.ToolEvents?.removeListener('selection', (selection: HsbEntity[]) => {
        //     self.calculateCentreOfMass(selection);
        // });
    }

    /**
     * Calculates the centre of mass for the given selection
     * @param selection The given selection of visuals
     */
    public calculateCentreOfMass(selection?: HsbEntity[]) {
        let self = this;
        if (self._viewer.currentControls?.pivotMethod !== 'mass') return;
        let box: THREE.Box3 | null = null;
        if (selection) {
            // Get all obejcts from selected entities
            let objects: THREE.Object3D[] = selection.filter((e) => e.object).map((e) => e.object!);
            switch (selection?.length) {
                case 0:
                    break; // No object thus no bounding box
                case 1: {
                    // Set box for only object
                    box = new THREE.Box3().setFromObject(objects[0]);
                    break;
                }
                // Else create bounding box for all objects
                default: {
                    box = objects.reduce((accu: THREE.Box3, object) => {
                        if (accu.isEmpty())
                            // No union if first object
                            return accu.setFromObject(object);
                        return accu.expandByObject(object);
                    }, new THREE.Box3().makeEmpty());
                }
            }
        }
        if (!box) return;
        let centerOfMass: THREE.Vector3 = box?.getCenter(new THREE.Vector3());
        if (self._viewer.currentControls) self._viewer.currentControls.pivotCenter = centerOfMass;
    }

    /**
     * Sets the current camera's local direction based on an entity's local space. Use @method setWorlDirection() for a world version.
     * @param direction Desired direction as Vector3 in worldspace
     * @param entity The entity of which the local space should be used.
     * @param tween Set true if a tween is desired.
     * @param time (Optional) desired duration of the tween. Will default to 1 sec if undefined.
     * @returns If 'tween' is true: Promise that resolves when ended. Promise will resolve true if completed, false if aborted. If {{tween}} is undefined or false this method will be void.
     */
    public setLocalDirection(
        entity,
        direction: THREE.Vector3,
        tween: boolean = false,
        time?: number
    ) {

        
        if (entity?.object) {
            direction = entity?.object
                .localToWorld(direction.clone())
                .sub(entity?.object.getWorldPosition(new THREE.Vector3()));
        }
        return this.setWorlDirection(direction, tween, time);
    }

    /**
     * Sets the current camera's world direction. Use @method setLocalDirection() for a local version.
     * @param direction Desired direction as Vector3 in worldspace
     * @param tween Set true if a tween is desired.
     * @param time (Optional) desired duration of the tween. Will default to 1 sec if undefined.
     * @returns If {{tween}} is true: Promise that resolves when ended. Promise will resolve true if completed, false if aborted. If {{tween}} is undefined or false this method will be void.
     */
    public setWorlDirection(direction: THREE.Vector3, tween: boolean = false, time?: number) {
        this.stopActiveOperation();
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;

        if (tween) return this.startTweenDirection(direction, time);
        else {
            var delta = this._viewer.currentCamera.position
                .clone()
                .sub(this._viewer.currentControls.target);
            var length = delta.length();
            var pos = this._viewer.currentControls.target
                .clone()
                .add(direction.clone().multiplyScalar(length));
            this._viewer.currentCamera.position.copy(pos);
            this._viewer.currentCamera.lookAt(this._viewer.currentControls.target);
            this._viewer.currentCamera.updateMatrix();
            this._viewer.currentControls.update();
        }
    }

    /**
     * Sets the current camera's world direction and position.
     * @param direction Desired direction as Quaternion
     * @param pos Desired world position as Vector3
     * @param tween Set true if a tween is desired.
     * @param time (Optional) desired duration of the tween. Will default to 1 sec if undefined.
     * @returns If {{tween}} is true: Promise that resolves when ended. Promise will resolve true if completed, false if aborted. If {{tween}} is undefined or false this method will be void.
     */
    public setWorldDirAndPos(
        direction: THREE.Quaternion,
        pos: THREE.Vector3,
        tween: boolean = false,
        time?: number
    ) {
        this.stopActiveOperation();
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;

        if (tween) return this.startTweenDirPos(direction, pos, time);
        else {
            this._viewer.currentCamera.position.copy(pos);
            this._viewer.currentCamera.setRotationFromQuaternion(direction);
            this._viewer.currentCamera.updateMatrix();
            this._viewer.currentControls.update();
        }
    }

    /**
     * Sets the current camera to the given camera view.
     * @param cameraView The desired camera view
     * @param tween True if a tween should be performed
     * @param time (Optional) desired duration of the tween. Will default to 1 sec if undefined.
     * @returns If {{tween}} is true: Promise that resolves when ended. Promise will resolve true if completed, false if aborted. If {{tween}} is undefined or false this method will be void.
     */
    public setCameraView(cameraView: CameraView, tween: boolean = false, time?: number) {
        this.stopActiveOperation();
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;
        if (tween) {
            return this.startTweenCameraView(cameraView, time);
        } else {
            var view = cameraView.view;
            if (view.matrix?.elements) {
                var matrix = new THREE.Matrix4();
                matrix.elements = view.matrix.elements;
                var pos = new THREE.Vector3(),
                    rot = new THREE.Quaternion(),
                    scale = new THREE.Vector3();
                matrix.decompose(pos, rot, scale);
                this._viewer.currentCamera.position.copy(pos);
                this._viewer.currentCamera.quaternion.copy(rot);
            }
            if (view.zoom) this._viewer.currentCamera.zoom = view.zoom;
            if (view.target) this._viewer.currentControls.target.copy(view.target);
            this._viewer.currentCamera.updateMatrix();
            this._viewer.currentControls.update();
        }
    }

    /**
     * Starts tween for the given position
     * @param position Desired position at the end of the tween.
     * @param time (Optional) desired duration for the tween. Will default to 1sec if undefined.
     * @returns Promise that resolves when ended. Promise will resolve true if completed, false if aborted.
     */
    private startTweenPosition(position: THREE.Vector3, time?: number) {
        this.stopActiveOperation();
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;

        this._tweenFocus = this._viewer.currentControls.target;
        var v1 = this._viewer.currentCamera.position;
        this._tweenPath = new THREE.LineCurve3(v1, position);
        this._tween = 0;
        this._viewer.currentControls.enableDamping = false;
        this._tweenInterval = setInterval(
            this.tweenCameraAlongPath.bind(this, time),
            ((time ? time : this._tweenTime) * 1000) / this._tweenStep
        );
        let self = this;
        return (this._tweenPromise = new Promise((resolve) => {
            // If abort event
            let abortSub = self.abortEvent.once('abort', () => {
                resolve(false);
            });
            // If timer completed
            self._tweenTimeout = setTimeout(() => {
                self.reset.bind(self);
                abortSub.removeAllListeners();
                resolve(true);
            }, (time ? time : self._tweenTime) * 1000);
        }));
    }

    /**
     * Starts a tween operation for the given direction
     * @param direction Desired direction at the end of the tween.
     * @param time (Optional) duration for the tween. Will default to 1sec if undefined
     * @returns Promise that resolves when ended. Promise will resolve true if completed, false if aborted.
     */
    private startTweenDirection(direction: THREE.Vector3, time?: number): Promise<any> {
        this.stopActiveOperation();
        if (!this._viewer.currentCamera || !this._viewer.currentControls)
            return Promise.reject('No active camera and/or controls!');

        var delta = this._viewer.currentCamera.position
            .clone()
            .sub(this._viewer.currentControls.target);
        var length = delta.length();
        let spherical = new THREE.Spherical().setFromVector3(direction).makeSafe();
        direction.setFromSpherical(spherical);
        this._tweenFocus = this._viewer.currentControls.target;
        var v1 = this._viewer.currentCamera.position;
        var v2 = this._tweenFocus.clone().add(direction.clone().multiplyScalar(length));
        let plane = new THREE.Plane().setFromNormalAndCoplanarPoint(direction.clone(), v2);
        var vC = plane.projectPoint(v1, new THREE.Vector3());
        this._tweenPath = new THREE.QuadraticBezierCurve3(v1, vC, v2);
        this._tween = 0;
        this._viewer.currentControls.enableDamping = false;
        this._tweenInterval = setInterval(
            this.tweenCameraAlongPath.bind(this, time),
            ((time ? time : this._tweenTime) * 1000) / this._tweenStep
        );
        let self = this;
        return (this._tweenPromise = new Promise((resolve) => {
            // If abort event
            let abortSub = self.abortEvent.once('abort', () => {
                resolve(false);
            });
            // If timer completed
            self._tweenTimeout = setTimeout(() => {
                self.reset.bind(self);
                abortSub.removeAllListeners();
                resolve(true);
            }, (time ? time : self._tweenTime) * 1000);
        }));
    }

    /**
     * Starts a tween operation for the given direction and position.
     * @param targetRot Desired rotation at the end of the tween.
     * @param targetPos Desired position at the end of the tween.
     * @param time (Optional) duration of the tween. Will use default 1s if undefined.
     * @returns Promise that resolves when ended. Promise will resolve true if completed, false if aborted.
     */
    public startTweenDirPos(
        targetRot: THREE.Quaternion,
        targetPos: THREE.Vector3,
        time?: number
    ): Promise<any> {
        this.stopActiveOperation();
        if (!this._viewer.currentCamera) return Promise.reject('No active camera!');

        this.startPos = this._viewer.currentCamera.position;
        this.targetPos = targetPos;
        this.startRot = this._viewer.currentCamera.quaternion;
        this.targetRot = targetRot;
        this._tweenAttributes.set(TweenAttributes.POSITION | TweenAttributes.ROTATION);
        let actualTime = time ? time : this._tweenTime;
        this._viewer.ViewerEvents?.CameraEvent.emit('start');
        this._tweenInterval = setInterval(
            this.tweenCamera.bind(this, actualTime),
            (actualTime * 1000) / this._tweenStep
        );
        let self = this;
        return (this._tweenPromise = new Promise((resolve) => {
            // If abort event
            let abortSub = self.abortEvent.once('abort', () => {
                resolve(false);
                self._viewer.ViewerEvents?.CameraEvent.emit('end');
            });
            // If timer completed
            self._tweenTimeout = setTimeout(() => {
                self.reset.bind(self);
                abortSub.removeAllListeners();
                resolve(true);
            }, (time ? time : self._tweenTime) * 1000);
        }));
    }

    /**
     * Starts a tween operation for the given camera view.
     * @param cameraView The desired cameraView at the end of the tween.
     * @param time (Optional) duration of tween. Will use this.tweenTime if undefined.
     * @returns Promise that resolves when ended. Promise will resolve true if completed, false if aborted.
     */
    private startTweenCameraView(cameraView: CameraView, time?: number) {
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;
        this._tweenAttributes.clear();
        var view = cameraView.view;
        if (view.matrix?.elements) {
            var matrix = new THREE.Matrix4();
            matrix.elements = view.matrix.elements;
            var scale = new THREE.Vector3();
            this.deltaPos = [new THREE.Vector3(), new THREE.Vector3()];
            this.deltaRot = [new THREE.Quaternion(), new THREE.Quaternion()];
            matrix.decompose(this.deltaPos[1], this.deltaRot[1], scale);
            this.deltaPos[0].copy(this._viewer.currentCamera.position);
            this.deltaRot[0].copy(this._viewer.currentCamera.quaternion);
            this._tweenAttributes.addFlag(TweenAttributes.POSITION | TweenAttributes.ROTATION);
        }
        if (view.zoom) {
            this.deltaZoom = [this._viewer.currentCamera.zoom, view.zoom];
            this._tweenAttributes.addFlag(TweenAttributes.ZOOM);
        }
        if (view.target) {
            this.deltaTarget = [
                this._viewer.currentControls.target.clone(),
                new THREE.Vector3(view.target.x, view.target.y, view.target.z),
            ];
            this._tweenAttributes.addFlag(TweenAttributes.TARGET);
        }

        this._tween = 0;
        this._viewer.currentControls.enableDamping = false;
        this.frameTiming = performance.now();
        let actualTime = time || this._tweenTime;
        this._viewer.ViewerEvents?.CameraEvent.emit('start');
        this._tweenInterval = setInterval(
            this.tweenCamera.bind(this, actualTime),
            (actualTime * 1000) / this._tweenStep
        );
        let self = this;
        return (this._tweenPromise = new Promise((resolve) => {
            // If abort event
            let abortSub = self.abortEvent.once('abort', () => {
                resolve(false);
                self._viewer.ViewerEvents?.CameraEvent.emit('end');
            });
            // If timer completed
            self._tweenTimeout = setTimeout(() => {
                self.reset();
                abortSub.removeAllListeners();
                resolve(true);
            }, (time || self._tweenTime) * 1000);
        }));
    }

    /**
     * Check and stops active tween operations if defined.
     */
    private stopActiveOperation() {
        if (this._tweenInterval) {
            clearInterval(this._tweenInterval);
            this._tweenInterval = null;
        }

        if (this._tweenPromise) {
            if (this._tweenTimeout !== null) clearTimeout(this._tweenTimeout);
            this._tweenTimeout = null;
            this.abortEvent.emit('abort');
        }
    }

    /**
     * Rests variables when tween has stopped.
     */
    private reset() {
        clearInterval(this._tweenInterval);
        this._tweenInterval = null;
        this.frame = 0;
        this._tweenFocus = null;
        this._tween = 0;
        this._tweenPath = null;
        this._tweenAttributes.clear();
        this.frameTiming = 0;
        if (this._tweenTimeout !== null) clearTimeout(this._tweenTimeout);
        this._tweenTimeout = null;
    }

    public zoomCamera(zoom: number) {
        let camera = this._viewer.currentCamera;
        if (camera instanceof THREE.PerspectiveCamera) {
            camera.zoom = zoom;
        } else if (camera instanceof THREE.OrthographicCamera) {
            camera.top /= zoom;
            camera.left /= zoom;
            camera.right /= zoom;
            camera.bottom /= zoom;
        }
    }

    /**
     * Sets the correct tween depending on timing.
     * @param time
     */
    private calculateTween(time?: number) {
        var currentTiming = performance.now();
        this._tween +=
            (0.001 * (currentTiming - this.frameTiming)) / (time ? time : this._tweenTime);
        this._tween = Math.min(this._tween, 1);
        this.frameTiming = currentTiming;
    }

    /**
     * Sets the camera for the set attributes and the given tween.
     * @param time
     */
    private tweenCamera(time?: number, onComplete?: () => any) {
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;
        this.frame++;
        this.calculateTween(time);
        let camera = this._viewer.currentCamera;
        if (this._tweenAttributes.hasFlag(TweenAttributes.ROTATION) && this.deltaRot) {
            let rot = this.deltaRot[0].clone().slerp(this.deltaRot[1], this._tween);
            camera.setRotationFromQuaternion(rot);
        }

        if (
            this._tweenAttributes.hasFlag(TweenAttributes.SPHERICAL) &&
            this.deltaSpherical &&
            this.deltaTarget
        ) {
            let { phi: phi0, theta: theta0, radius: radius0 } = this.deltaSpherical[0].clone();
            let { phi: phi1, theta: theta1, radius: radius1 } = this.deltaSpherical[1].clone();
            let spherical = new THREE.Spherical(
                lerp(radius0, radius1, this._tween),
                lerp(phi0, phi1, this._tween),
                lerp(theta0, theta1, this._tween)
            );
            let pos = this.deltaTarget[1]
                .clone()
                .add(new THREE.Vector3().setFromSpherical(spherical));
            camera.position.copy(pos);
        }

        if (this._tweenAttributes.hasFlag(TweenAttributes.POSITION) && this.deltaPos) {
            let pos = this.deltaPos[0].clone().lerp(this.deltaPos[1], this._tween);
            camera.position.copy(pos);
        }

        if (this._tweenAttributes.hasFlag(TweenAttributes.ZOOM) && this.deltaZoom) {
            let zoom = THREE.MathUtils.lerp(this.deltaZoom[0], this.deltaZoom[1], this._tween);
            camera.zoom = zoom;
        }

        if (this._tweenAttributes.hasFlag(TweenAttributes.TARGET) && this.deltaTarget) {
            let pos = this.deltaTarget[0].clone().lerp(this.deltaTarget[1], this._tween);
            this._viewer.currentControls.target.copy(pos);
        }

        if (this._tweenAttributes.hasFlag(TweenAttributes.UP) && this.deltaUp) {
            let curr = camera.up.clone();
            let up = curr.lerp(this.deltaUp[1], this._tween);
            camera.up.copy(up);
        }

        camera.updateMatrix();
        camera.updateProjectionMatrix();
        this._viewer.ViewerEvents?.CameraEvent.emit('change');
        this._viewer.requestRenderFrame();
        if (!this._tweenAttributes.hasFlag(TweenAttributes.ROTATION))
            this._viewer.currentControls.update();

        if (this._tween >= 1) {
            this.reset();
            this._viewer.ViewerEvents?.CameraEvent.emit('end');
            if (onComplete) {
                onComplete();
            }
        }
    }
    /**
     * Performs tween for new desired camera position.
     */
    private tweenCameraAlongPath(time?: number) {
        if (!this._viewer.currentCamera || !this._viewer.currentControls) return;
        if (!this._tweenPath) return;

        this.frame++;
        this.calculateTween(time);

        let current = this._tweenPath.getPointAt(this._tween);
        this._viewer.currentCamera.position.copy(current);
        this._viewer.currentCamera.lookAt(this._tweenFocus);
        this._viewer.currentCamera.updateMatrix();
        this._viewer.currentControls.update();
    }

    private getAABBfromEntities(entities: HsbEntity[]) {
        let bBoxes = entities
            .filter((e) => e.object)
            .map((ent) => {
                let aabb = new THREE.Box3();
                if (ent.object instanceof THREE.InstancedMesh) {
                    let vertices = (ent.object.geometry as THREE.BufferGeometry).getAttribute(
                        'position'
                    ) as THREE.BufferAttribute;
                    let m4 = new THREE.Matrix4();
                    ent.object.getMatrixAt(ent.instanceId, m4);
                    m4 = m4.multiply(ent.object.matrixWorld.clone());
                    let pos = new THREE.Vector3().setFromMatrixPosition(m4);
                    aabb.setFromBufferAttribute(vertices);
                    aabb.translate(new THREE.Vector3(pos.x, pos.z, -pos.y));
                } else if (ent.object) {
                    aabb.setFromObject(ent.object);
                }
                return aabb;
            });

        var bBox: THREE.Box3 = bBoxes[0];
        if (bBoxes.length > 1) {
            for (let index = 1; index < bBoxes.length; index++) {
                const box = bBoxes[index];
                bBox = bBox.union(box);
            }
        }
        return bBox;
    }

    public FocusCameraInDirection(props: Partial<FocusOptions>) {
        // Getting Center of either selection or the visible entities.
        if (this._viewer.currentSelection?.length > 0) {
            var bBox = this.getAABBfromEntities(this._viewer.currentSelection);
        } else if (props?.scene) {
            var bBox = new THREE.Box3().setFromObject(props.scene);
        } else {
            let entityLayer = this._viewer!.currentScene!.GetLayer(SceneLayerName.Entities);
            if (!entityLayer || entityLayer.children.length === 0) return;
            var bBox = new THREE.Box3().makeEmpty();
            entityLayer.children.forEach((c) => bBox.expandByObject(c));
        }
        // If no bounds
        if (!bBox) return Promise.reject('CameraUtils.FocusCameraInDirection(): No AABB!');
       
        // If no direction given
        if (!props.direction && !props.sides)
            return Promise.reject(
                'CameraUtils.FocusCameraInDirection(): No Sides or direction provided!'
            );
        return this.focusCamera(bBox, props);
    }

    /**
     * Focuses the camera on the given object.
     * @param objects The obejcts to focus on
     */
    public FocusCameraOnObject(
        objects: THREE.Object3D | THREE.Object3D[],
        options?: Partial<FocusOptions>
    ) {
        var bbox: THREE.Box3;
        if (Array.isArray(objects)) {
            if (objects.length === 0) return;
            bbox = new THREE.Box3().makeEmpty();
            objects[0].updateMatrixWorld();
            objects.forEach((obj) => {
                bbox.expandByObject(obj);
            });
        } else {
            if (!objects) return;
            bbox = new THREE.Box3().setFromObject(objects);
        }
        if (bbox) return this.focusCamera(bbox, options);
        return Promise.reject('CameraUtils.FocusCameraOnObject(): No AABB!');
    }

    /**
     * Focuses the camera on the given object.
     * @param entities The enetities to focus on
     */
    public FocusCameraOnEntities(entities, options?: Partial<FocusOptions>) {
        let bbox = this.getAABBfromEntities(Array.isArray(entities) ? entities : [entities]);
        if (bbox) return this.focusCamera(bbox, options);
    }

    public FocusCameraOnVisuals(visuals: HsbVisual[] | HsbVisual, options?: Partial<FocusOptions>) {
        var bbox: THREE.Box3 = new THREE.Box3().makeEmpty();
        if (Array.isArray(visuals)) {
            if (visuals.length === 0) return;
            visuals.forEach((visual) => {
                if (bbox.isEmpty()) bbox.setFromObject(visual.geometryModel);
                else bbox.expandByObject(visual.geometryModel);
            });
        } else {
            if (!visuals) return;
            bbox.setFromObject(visuals.geometryModel);
        }
        if (bbox) return this.focusCamera(bbox, options);
        return Promise.reject('CameraUtils.FocusCameraOnVisuals(): No AABB!');
    }

    /**
     * Converts the given Sides enum into a unit THREE.Vector3
     * @param {Sides} sides The given sides to convert into a vector3
     * @returns {THREE.Vector3} A THREE.Vector3 for the given sides
     */
    private getDirection(sides: Sides) {
        if (!this._viewer.currentCamera) return;

        if (sides === null || sides === undefined) return null;
        let dir = new THREE.Vector3();
        if ((Sides.Left & sides) === Sides.Left) dir.x = -1;
        if ((Sides.Right & sides) === Sides.Right) dir.x = 1;
        if ((Sides.Top & sides) === Sides.Top) dir.y = 1;
        if ((Sides.Bottom & sides) === Sides.Bottom) dir.y = 1;
        if ((Sides.Front & sides) === Sides.Front) dir.z = 1;
        if ((Sides.Back & sides) === Sides.Back) dir.z = -1;
        //Up lock
        if (this._viewer.currentCamera.up.dot(dir) > 0.9999) dir.z += 0.0001;
        return dir;
    }

    public focusCamera(bBox: THREE.Box3, options?: Partial<FocusOptions>) {
        if (!this._viewer.currentCamera) return;
        let { offset, tween, time, direction, sides } = options ?? { offset: 25 };
        if (!bBox) {
            console.warn('CameraUtils::focusCamera(): bBox was null. Focus was aborted.');
            return;
        }
        // Access current controls
        var camera = options?.camera ?? options?.scene?.currentCamera ?? this._viewer.currentCamera;
        var controls = options?.controls ?? options?.scene?.CameraControls ?? this._viewer.currentControls;
        // Bounds
        var center: THREE.Vector3 = new THREE.Vector3();
        var size: THREE.Vector3 = new THREE.Vector3();
        bBox.getCenter(center);
        bBox.getSize(size);
        var sphere: THREE.Sphere = new THREE.Sphere();
        bBox.getBoundingSphere(sphere);

        // Spherical coords
        // Get cartesian coords

        let cameraDir =
            direction?.normalize() ??
            (sides && this.getDirection(sides)?.normalize()) ??
            camera.getWorldDirection(new THREE.Vector3()).negate();

        let cart = camera.position.clone().sub(center);
        let originalSpherical = new THREE.Spherical()
            .setFromCartesianCoords(cart.x, cart.y, cart.z)
            .makeSafe();
        let targetSpherical = new THREE.Spherical()
            .setFromCartesianCoords(cameraDir.x, cameraDir.y, cameraDir.z)
            .makeSafe();
        if (targetSpherical.theta - originalSpherical.theta > Math.PI) {
            // Check clock or counter clock wise
            originalSpherical.theta += Math.PI * 2;
        }
        targetSpherical.radius = originalSpherical.radius;

        var targetPos: THREE.Vector3 = new THREE.Vector3();
        if (camera instanceof THREE.PerspectiveCamera) {
            //Get FOV for offset
            const fov = camera.fov * (Math.PI / 180);
            offset = (sphere.radius + (offset ?? 25)) / Math.tan(fov / 2);
            targetPos = center.clone().add(cameraDir.clone().multiplyScalar(offset));
        } else if (camera instanceof THREE.OrthographicCamera) {
            var viewSize = 2 * camera.top;
            var aspectRatio = (2 * camera.right) / viewSize;
            var radius = sphere.radius + (offset ?? 25);
            if (originalSpherical) originalSpherical.radius = (camera.far - camera.near) * 0.5;
            if (targetSpherical) targetSpherical.radius = (camera.far - camera.near) * 0.5;
            // targetPos = center.clone().add(cameraDir.clone().multiplyScalar(offset));
            targetPos = cameraDir
                .clone()
                .multiplyScalar((camera.far - camera.near) * 0.5)
                .add(center);
            // // Aspect ratio

            camera.left = -aspectRatio * radius;
            camera.right = aspectRatio * radius;
            camera.top = radius;
            camera.bottom = -radius;

            camera.updateProjectionMatrix();
            camera.updateMatrix();
        }
        // Start tween operation if desired
        if (tween) {
            this.stopActiveOperation();
            this._tween = 0;
            this.frameTiming = performance.now();
            // Spherical
            if (targetSpherical && originalSpherical) {
                this.deltaSpherical = [originalSpherical, targetSpherical];
                this._tweenAttributes.set(TweenAttributes.SPHERICAL);
            } else if (this.targetPos !== null) {
                this.deltaPos = [camera.position.clone(), targetPos];
                this._tweenAttributes.set(TweenAttributes.POSITION);
            }
            // // Zoom
            this.deltaZoom = [camera.zoom, 1];
            this._tweenAttributes.addFlag(TweenAttributes.ZOOM);
            // Setting the desired target
            if (controls) {
                this.deltaTarget = [controls.target.clone(), center.clone()];
                this._tweenAttributes.addFlag(TweenAttributes.TARGET);
            }

            let actualTime = time ?? this._tweenTime;
            let self = this;
            // Return resolve when tween is completed or aborted
            return (this._tweenPromise = new Promise((resolve) => {
                // Listen to abort if needed
                self.abortEvent.once('abort', () => {
                    clearInterval(this._tweenInterval);
                    // Incomplete tween
                    resolve(false);
                    self._viewer.ViewerEvents?.CameraEvent.emit('end');
                });
                self._viewer.ViewerEvents?.CameraEvent.emit('start');
                this._tweenInterval = setInterval(
                    this.tweenCamera.bind(this, actualTime, () => {
                        resolve(true);
                        self.abortEvent.removeAllListeners();
                    }),
                    (actualTime * 1000) / this._tweenStep
                );
            }));
        } else {
            // Simple case of instantly teleporting the camera
            camera.zoom = 1;
            // Assigning everything and updating
            camera.position.copy(targetPos);
            camera.updateProjectionMatrix();
            camera.updateMatrix();
            controls?.target.copy(center);
            controls?.update();
            return Promise.resolve();
        }
    }
}
